/**
 * What the assistant can do for the account currently signed in.
 *
 * The assistant is mounted app-wide for every logged-in user, but its skills are
 * not universal — location editing and the supplier finance questions run
 * against supplier-scoped endpoints. Rather than hide the assistant from
 * everyone else, each skill declares the audiences it serves and the assistant
 * reports honestly what it can do for *you*.
 *
 * This is the extension point for new skills. Adding a buyer or admin capability
 * means tagging a registry entry with that audience — no change to the gating,
 * the provider, or the conversation state machine.
 */

import { AccountType } from '../constants/securspace-constants';
import { hasAdminAccess } from '../../util/AccountUtils';

export type AssistantAudience = 'supplier' | 'buyer' | 'admin';

export interface AssistantAccount {
  type?: string;
  emailVerified?: boolean;
  /**
   * The signed-in *person's* role, which is not the same thing as the account
   * type. While an admin is impersonating a customer via "log in as account",
   * `type` becomes the customer's but this stays the admin role — the backend
   * derives it from the retained authorities, not from the target account
   * (see `AccountService.getLoggedInAccountForUsername`).
   */
  userType?: string;
}

export interface AssistantCapabilities {
  audience: AssistantAudience;
  /**
   * Whether the signed-in person is SecurSpace staff, regardless of which
   * account they are currently acting in.
   *
   * True for an admin who is impersonating a customer. `loginAsAccount` swaps
   * only the `ROLE_ACCOUNT_<uuid>` authority and retains the staff role, so such
   * a session still passes every `@IsAdmin` check on the server. Treating that
   * person as a customer would mean the flags meant to keep a prototype away
   * from customers also keep it away from the staff testing it.
   */
  isStaff: boolean;
  /**
   * True when staff are acting inside a customer account. Data stays scoped to
   * that customer — the point of impersonation — but the session's reach does
   * not, which is worth surfacing rather than leaving implicit.
   */
  isImpersonating: boolean;
  /**
   * Whether the location bulk-edit write path is available.
   *
   * Requires a verified email as well as a supplier account: the backend
   * refuses the write either way, so offering it would only produce a failure
   * after the user has composed and confirmed a change.
   */
  canEditLocations: boolean;
}

const audienceFor = (type?: string): AssistantAudience => {
  if (type === AccountType.SUPPLIER) return 'supplier';
  if (type === AccountType.ADMIN) return 'admin';
  return 'buyer';
};

export const capabilitiesFor = (account?: AssistantAccount | null): AssistantCapabilities => {
  const audience = audienceFor(account?.type);
  // The same `userType`-based admin check the impersonation banner uses, so the
  // assistant's notion of "staff" cannot drift from the rest of the app. The
  // runtime `userType` is the plain `UserType` form ('ADMIN'/'SUPER_ADMIN') —
  // not the `ROLE_USERTYPE_*` DB format — which is exactly what `hasAdminAccess`
  // matches.
  const isStaff = hasAdminAccess({ userType: account?.userType ?? '' });
  return {
    audience,
    isStaff,
    // Staff acting in an account that is not the admin account.
    isImpersonating: isStaff && audience !== 'admin',
    canEditLocations: audience === 'supplier' && account?.emailVerified === true,
  };
};
